import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import mimeTypes from './dropzoneMimeTypes.json';
import './Dropzone.css';

const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const formatBytes = (bytes) => {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
};

export default function Dropzone() {
    const [config, setConfig] = useState(null);
    const [configError, setConfigError] = useState(false);
    const [configAttempt, setConfigAttempt] = useState(0);
    const [files, setFiles] = useState([]);
    const [notices, setNotices] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyError, setCopyError] = useState(false);
    const input = useRef(null);
    const request = useRef(null);
    const stopped = useRef(false);
    const uploading = useRef(false);
    const mounted = useRef(true);
    const dragDepth = useRef(0);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            stopped.current = true;
            request.current?.abort();
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setConfigError(false);
        fetch(`${apiBase}/api/dropzone/config`, { signal: controller.signal, headers: { Accept: 'application/json' } })
            .then(response => {
                if (!response.ok) throw new Error('Unavailable');
                return response.json();
            })
            .then(data => {
                if (!Array.isArray(data.extensions) || !data.maxFileBytes || !data.maxFiles) throw new Error('Invalid config');
                setConfig(data);
            })
            .catch(error => { if (error.name !== 'AbortError') setConfigError(true); });
        return () => controller.abort();
    }, [configAttempt]);

    useEffect(() => {
        if (!busy) return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [busy]);

    const updateFile = (id, changes) => {
        if (mounted.current) setFiles(current => current.map(item => item.id === id ? { ...item, ...changes } : item));
    };

    const addFiles = (selection) => {
        if (!config || uploading.current) return;
        const warnings = [];
        const next = [...files];
        for (const file of Array.from(selection)) {
            const extension = file.name.split('.').pop().toLowerCase();
            if (!config.extensions.includes(extension)) {
                warnings.push(`${file.name}: unsupported format.`);
            } else if (!file.size) {
                warnings.push(`${file.name}: this file is empty.`);
            } else if (file.size > config.maxFileBytes) {
                warnings.push(`${file.name}: larger than ${formatBytes(config.maxFileBytes)}.`);
            } else if (next.some(item => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified)) {
                warnings.push(`${file.name}: already in your queue.`);
            } else if (next.length >= config.maxFiles) {
                warnings.push(`Select up to ${config.maxFiles} files at a time. Clear completed uploads to add more.`);
                break;
            } else {
                next.push({ id: `${Date.now()}-${next.length}`, file, status: 'queued', progress: 0 });
            }
        }
        setFiles(next);
        setNotices(warnings);
    };

    const uploadLocalFile = (item) => new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        request.current = xhr;
        xhr.open('POST', `${apiBase}/api/dropzone/uploads`);
        xhr.timeout = 60 * 60 * 1000;
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.setRequestHeader('X-File-Name', encodeURIComponent(item.file.name));
        xhr.setRequestHeader('X-File-Size', String(item.file.size));
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const progress = Math.round(event.loaded / event.total * 100);
                updateFile(item.id, { progress, status: progress === 100 ? 'saving' : 'uploading' });
            }
        };
        xhr.onload = () => {
            let response;
            try { response = JSON.parse(xhr.responseText); } catch { response = {}; }
            if (xhr.status === 201 && response.bytes === item.file.size && response.sha256) resolve(response);
            else reject(new Error(response.message || 'Upload could not be confirmed. Please try again.'));
        };
        xhr.onerror = () => reject(new Error('Connection lost. Check your connection and try again.'));
        xhr.ontimeout = () => reject(new Error('Upload timed out. Please try again on a faster connection.'));
        xhr.onabort = () => reject(new Error('Upload cancelled. You can try again.'));
        // Send the File itself: no canvas, resizing, compression or transcoding.
        xhr.send(item.file);
    });

    const uploadFile = async (item) => {
        if (config.provider !== 'vercel-blob') return uploadLocalFile(item);
        const controller = new AbortController();
        request.current = controller;
        const timeout = setTimeout(() => controller.abort(), 60 * 60 * 1000);
        try {
            // Load the cloud SDK only when the deployment uses Blob storage.
            const { upload } = await import('@vercel/blob/client');
            const extension = item.file.name.split('.').pop().toLowerCase();
            const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
            const stem = item.file.name.slice(0, -(extension.length + 1)).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 90) || 'original';
            // Keep the uploaded path on retries if only confirmation was interrupted.
            let blobPath = item.blobPath;
            if (!blobPath) {
                const result = await upload(`dropzone/${id}/${stem}.${extension}`, item.file, {
                    access: 'private',
                    contentType: mimeTypes[extension],
                    handleUploadUrl: `${apiBase}/api/dropzone/uploads`,
                    clientPayload: JSON.stringify({ originalName: item.file.name, bytes: item.file.size }),
                    multipart: item.file.size > 8 * 1024 ** 2,
                    abortSignal: controller.signal,
                    onUploadProgress: ({ percentage }) => {
                        const progress = Math.round(percentage);
                        updateFile(item.id, { progress, status: progress === 100 ? 'saving' : 'uploading' });
                    }
                });
                blobPath = result.pathname;
                updateFile(item.id, { blobPath: result.pathname, status: 'saving', progress: 100 });
            }
            const response = await fetch(`${apiBase}/api/dropzone/verify`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
                body: JSON.stringify({ pathname: blobPath, bytes: item.file.size })
            });
            const result = await response.json();
            if (!response.ok || !result.verified || result.bytes !== item.file.size) {
                if ([400, 404, 415].includes(response.status)) {
                    updateFile(item.id, { blobPath: undefined });
                }
                throw new Error(result.message || 'Upload could not be confirmed. Please try again.');
            }
        } catch (error) {
            if (controller.signal.aborted) throw new Error('Upload cancelled or timed out. You can try again.');
            throw error;
        } finally {
            clearTimeout(timeout);
        }
    };

    const startUpload = async (selection) => {
        if (uploading.current) return;
        const queue = selection || files.filter(item => item.status !== 'done');
        if (!queue.length) return;
        uploading.current = true;
        stopped.current = false;
        setBusy(true);
        setNotices([]);
        try {
            for (const item of queue) {
                if (stopped.current || !mounted.current) break;
                updateFile(item.id, { status: 'uploading', progress: 0, error: '' });
                try {
                    await uploadFile(item);
                    updateFile(item.id, { status: 'done', progress: 100 });
                } catch (error) {
                    updateFile(item.id, { status: 'error', error: error.message });
                }
            }
        } finally {
            request.current = null;
            uploading.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    const cancelUpload = () => {
        stopped.current = true;
        request.current?.abort();
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/dropzone`);
            setCopied(true);
            setCopyError(false);
        } catch { setCopyError(true); }
    };

    const complete = files.filter(item => item.status === 'done').length;
    const failed = files.filter(item => item.status === 'error').length;
    const totalBytes = files.reduce((sum, item) => sum + item.file.size, 0);
    const pending = files.length - complete;
    const status = busy ? 'TRANSFER IN PROGRESS' : complete && !pending ? 'TRANSFER COMPLETE' : config ? 'READY TO RECEIVE' : configError ? 'CONNECTION UNAVAILABLE' : 'CONNECTING';

    return (
        <div className="dropzone-page">
            <header className="dz-topbar">
                <Link to="/" onClick={event => busy && event.preventDefault()} aria-disabled={busy}>ROTB<span> / COMMUNITY ARCHIVE</span></Link>
                <button type="button" onClick={copyLink}>{copied ? '✓ LINK COPIED' : 'COPY DROPZONE LINK ↗'}</button>
            </header>

            <main className="dz-main">
                <div className="dz-heading">
                    <p className="dz-eyebrow">[ THE NIGHT DOESN’T END HERE ]</p>
                    <h1>DROPZONE<span aria-hidden="true">_</span></h1>
                    <p className="dz-intro">You were there. Let us see it through your eyes.<br />Drop your photos & videos from the dancefloor.</p>
                </div>

                {copyError && <p className="dz-notice" role="status">Copy this link: {window.location.origin}/dropzone</p>}

                <div className="dz-layout">
                    <section className="dz-transfer" aria-label="Upload photos and videos">
                        <div className="dz-panel-header"><span>01 / TRANSMIT YOUR MEMORIES</span><span className="dz-live" aria-hidden="true">●</span></div>
                        <div className={`dz-target ${dragging ? 'is-dragging' : ''} ${busy ? 'is-busy' : ''}`}
                            onDragEnter={event => { event.preventDefault(); dragDepth.current++; if (!busy && config) setDragging(true); }}
                            onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = busy ? 'none' : 'copy'; }}
                            onDragLeave={event => { event.preventDefault(); dragDepth.current--; if (dragDepth.current <= 0) setDragging(false); }}
                            onDrop={event => { event.preventDefault(); dragDepth.current = 0; setDragging(false); addFiles(event.dataTransfer.files); }}>
                            <div className="dz-upload-symbol" aria-hidden="true">↑</div>
                            <h2>{dragging ? 'RELEASE TO ADD' : 'DROP YOUR FILES HERE'}</h2>
                            <p>Every angle. Every moment. Original quality.</p>
                            <button type="button" className="dz-button dz-primary" disabled={!config || busy} onClick={() => input.current?.click()}>+ SELECT PHOTOS & VIDEOS</button>
                            <input ref={input} className="dz-file-input" type="file" multiple
                                aria-label="Select photos and videos"
                                accept={config ? config.extensions.map(extension => `.${extension}`).join(',') : 'image/*,video/*'}
                                disabled={!config || busy}
                                onChange={event => { addFiles(event.target.files); event.target.value = ''; }} />
                            <span className="dz-target-note">{config ? `UP TO ${formatBytes(config.maxFileBytes)} / FILE · ${config.maxFiles} FILES / BATCH` : 'CONNECTING TO THE DROPZONE…'}</span>
                        </div>

                        {configError && <div className="dz-notice" role="alert">The dropzone is unavailable. Please try again.<button type="button" className="dz-text-button" onClick={() => setConfigAttempt(value => value + 1)}>RECONNECT ↻</button></div>}
                        {!!notices.length && <div className="dz-notice" role="alert"><ul>{notices.map((notice, index) => <li key={index}>{notice}</li>)}</ul></div>}

                        <div className="dz-queue-heading"><h2>TRANSFER QUEUE <span>[ {String(files.length).padStart(2, '0')} ]</span></h2><span>{files.length ? formatBytes(totalBytes) : 'NO FILES YET'}</span></div>
                        {!files.length ? <div className="dz-empty"><span aria-hidden="true">[ … ]</span><p>Your memories go here.<br />Select files to start a transfer.</p></div> : (
                            <ul className="dz-file-list">{files.map(item => (
                                <li key={item.id} className={`dz-file dz-file-${item.status}`}>
                                    <span className="dz-file-kind" aria-hidden="true">{['mp4', 'mov', 'm4v', 'webm'].includes(item.file.name.split('.').pop().toLowerCase()) ? 'VID' : 'IMG'}</span>
                                    <div className="dz-file-info">
                                        <span className="dz-file-name" title={item.file.name}>{item.file.name}</span>
                                        <span className="dz-file-detail">{formatBytes(item.file.size)} · {item.status === 'done' ? 'ORIGINAL SAVED ✓' : item.status === 'saving' ? 'SAVING ORIGINAL…' : item.status === 'uploading' ? `UPLOADING ${item.progress}%` : item.status === 'error' ? 'TRANSFER FAILED' : 'READY'}</span>
                                        {['uploading', 'saving'].includes(item.status) && <progress max="100" value={item.progress} aria-label={`Upload progress for ${item.file.name}`} />}
                                        {item.error && <span className="dz-file-error">{item.error}</span>}
                                    </div>
                                    <div className="dz-file-actions">
                                        {item.status === 'error' && <button type="button" disabled={busy} onClick={() => startUpload([item])} aria-label={`Retry ${item.file.name}`}>RETRY</button>}
                                        <button type="button" disabled={busy} onClick={() => setFiles(current => current.filter(file => file.id !== item.id))} aria-label={`Remove ${item.file.name} from queue`}>×</button>
                                    </div>
                                </li>
                            ))}</ul>
                        )}
                        {!!files.length && <div className="dz-queue-footer">
                            <button type="button" className="dz-text-button" disabled={busy} onClick={() => { setFiles(current => current.filter(item => item.status !== 'done')); setNotices([]); }}>CLEAR COMPLETED</button>
                            {busy ? <button type="button" className="dz-button" onClick={cancelUpload}>CANCEL TRANSFER</button> : <button type="button" className="dz-button dz-primary" disabled={!pending} onClick={() => startUpload()}>↑ UPLOAD {pending || ''} {pending === 1 ? 'FILE' : 'FILES'}</button>}
                        </div>}
                        <div className="dz-status" role="status" aria-live="polite"><span className={busy ? 'dz-status-dot is-active' : 'dz-status-dot'} aria-hidden="true" /><span>{status}{!!complete && ` / ${complete} SAVED`}{!!failed && !busy && ` / ${failed} TO RETRY`}</span></div>
                    </section>

                    <aside className="dz-aside" aria-label="About your upload">
                        <div className="dz-quality"><span className="dz-eyebrow">NO FILTER BETWEEN US.</span><strong>100%</strong><span className="dz-quality-label">ORIGINAL QUALITY</span><p>Full resolution. Full detail.<br />Exactly as you captured it.</p></div>
                        <div className="dz-info-block"><span className="dz-info-number">[ 01 ]</span><h2>KEEP IT ORIGINAL</h2><p>Choose the original files from your device. We never resize photos or compress videos.</p><p className="dz-small">If your device offers format options, choose the original format to avoid conversion before uploading.</p></div>
                        <div className="dz-info-block"><span className="dz-info-number">[ 02 ]</span><h2>ALL YOUR ANGLES</h2><p>JPG, PNG, HEIC, HEIF, WebP, GIF, AVIF, TIFF.<br />MP4, MOV, M4V & WebM.</p></div>
                        <div className="dz-info-block"><span className="dz-info-number">[ 03 ]</span><h2>FOR THE MEMORIES</h2><p>Your files go to the ROTB team. They won’t appear in the public gallery automatically.</p><p className="dz-small">Keep this page open while uploading. A good Wi-Fi connection helps with larger videos.</p></div>
                    </aside>
                </div>
                <div className="dz-bottom"><span>FROM THE FLOOR. FOR THE ARCHIVE.</span><Link to="/gallery" onClick={event => busy && event.preventDefault()} aria-disabled={busy}>← BACK TO GALLERY</Link></div>
            </main>
        </div>
    );
}
